import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useLive, useRuntime, useUser } from './context';
import { useHasModule, useRemote } from './useRemote';
import { ApiFailure } from '../api/errors';
import { formatMoney } from '../mirror/time';
import {
  NewsletterIssue,
  NewsletterIssueList,
  NewsletterPreview,
  NewsletterSendResult,
  NewsletterSender,
} from '../wire';
import type { NewsletterBlock, NewsletterIssue as Issue, Product } from '../wire';
import { useLocale, useT } from '../messages';
import type { T, UiLocale } from '../messages';

/**
 * The newsletter composer — E3, and the answer to one question the owner asked
 * out loud: *"But don't I need skills on how to make it pretty?"*
 *
 * **No.** There is no font menu, no colour picker, no layout and no blank
 * canvas. There are six blocks — a heading, a paragraph, a picture, a product,
 * a button, a line — and the email is drawn from her own website's branding on
 * the server. Every visual decision was made once, in `@yourstore/email`. What
 * she supplies is words, pictures and which products, which is what she has.
 *
 * ## Online-only, deliberately
 *
 * `ClientPurchases`' precedent and its argument (`ui/useRemote.ts`): the mirror
 * holds *the book* — what the counter works on and can queue writes against. An
 * issue is neither. It is also not something anybody should be able to half-
 * send from a tunnel, which is the one thing an outbox would make possible.
 *
 * ## What is not here, and is not an oversight
 *
 * No open rate and no click tracking. She did not ask who read her mail; a
 * tracking pixel is a surveillance device her customers did not consent to; and
 * a pixel plus a link-rewriting domain are two of the strongest spam signals a
 * small sender can emit. The per-issue panel counts what the provider told us
 * happened to the *message*.
 */

type Draft = {
  subject: string;
  preheader: string;
  blocks: NewsletterBlock[];
};

const EMPTY: Draft = { subject: '', preheader: '', blocks: [] };

export function Newsletter(): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const entitled = useHasModule('clients');
  const [openId, setOpenId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const sender = useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: '/newsletter/sender',
        schema: NewsletterSender,
        signal,
      }),
    [],
    { enabled: entitled },
  );

  const issues = useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: '/newsletter/issues',
        schema: NewsletterIssueList,
        signal,
      }),
    [],
    { enabled: entitled },
  );

  const create = useCallback(async () => {
    try {
      const made = await runtime.api.request({
        method: 'POST',
        path: '/newsletter/issues',
        body: { subject: t('newsletter.newSubject') },
        schema: NewsletterIssue,
      });
      setOpenId(made.issue_id);
      await issues.reload();
    } catch (e) {
      setNotice(e instanceof ApiFailure ? e.message : String(e));
    }
  }, [runtime, issues, t]);

  if (!entitled) {
    return (
      <section className="view">
        <p className="empty">{t('newsletter.notSwitchedOn')}</p>
      </section>
    );
  }

  const open = issues.data?.issues.find((i) => i.issue_id === openId) ?? null;
  if (open !== null) {
    return (
      <IssueEditor
        issue={open}
        onClose={() => {
          setOpenId(null);
          void issues.reload();
        }}
      />
    );
  }

  const audience = sender.data?.audience.total ?? issues.data?.audience.total ?? 0;

  return (
    <section className="view">
      <div className="view__header">
        <h1>{t('tabs.newsletter')}</h1>
        <button type="button" className="button button--primary" onClick={() => void create()}>
          {t('newsletter.write')}
        </button>
      </div>

      {notice === null ? null : <p className="error">{notice}</p>}

      <SenderPanel sender={sender.data ?? null} audience={audience} />

      {issues.loading && issues.fetchedAt === null ? (
        <p className="empty">{t('newsletter.reading')}</p>
      ) : null}
      {issues.error !== null ? <p className="error">{t('newsletter.couldNotReach')}</p> : null}

      {(issues.data?.issues ?? []).length === 0 && !issues.loading ? (
        <p className="empty">{t('newsletter.noneYet')}</p>
      ) : (
        <ul className="list">
          {(issues.data?.issues ?? []).map((issue) => (
            <li key={issue.issue_id}>
              <button
                type="button"
                className="link-button"
                onClick={() => setOpenId(issue.issue_id)}
              >
                {issue.subject}
              </button>
              <span className={`badge badge--${issue.status}`}>
                {t(`newsletter.status.${issue.status}` as 'newsletter.status.draft')}
              </span>
              {issue.status === 'sent' || issue.status === 'sending' ? (
                <span className="muted">
                  {t('newsletter.counts', {
                    sent: issue.stats.sent,
                    failed: issue.stats.failed + issue.stats.bounced,
                    unsubscribed: issue.stats.unsubscribed,
                  })}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Who this business's email comes from, and the list it would go to.
 *
 * It is the first thing on the screen because the first fact an owner with no
 * list needs is that she has no list — and the second is the link to put on a
 * card so she starts getting one. `blockers` are printed **verbatim**: the
 * server is the only thing that knows which of eight reasons applies, and this
 * app's own boundary rule is that server sentences are never re-keyed.
 */
function SenderPanel({
  sender,
  audience,
}: {
  sender: NewsletterSender | null;
  audience: number;
}): ReactNode {
  const t = useT();
  if (sender === null) return null;

  return (
    <div className="panel panel--muted">
      <p>
        <strong>{t('newsletter.audience', { count: audience })}</strong>
      </p>
      {sender.from === '' ? null : (
        <p className="muted">{t('newsletter.from', { from: sender.from })}</p>
      )}
      {sender.reply_to === null ? null : (
        <p className="muted">{t('newsletter.replyTo', { to: sender.reply_to })}</p>
      )}
      {sender.join_url === null ? null : (
        <p className="muted">
          {t('newsletter.joinLink')} <code>{sender.join_url}</code>
        </p>
      )}
      {sender.blockers.map((sentence) => (
        <p className="error" key={sentence}>
          {sentence}
        </p>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* One issue                                                           */
/* ------------------------------------------------------------------ */

function IssueEditor({ issue, onClose }: { issue: Issue; onClose: () => void }): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const locale = useLocale();
  const [draft, setDraft] = useState<Draft>({
    subject: issue.subject,
    preheader: issue.preheader,
    blocks: issue.blocks,
  });
  const [saved, setSaved] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [preview, setPreview] = useState<NewsletterPreview | null>(null);
  const [width, setWidth] = useState<'desktop' | 'phone'>('desktop');

  const readOnly = issue.status !== 'draft' && issue.status !== 'scheduled';

  const products = useLive(() => runtime.mirror.products(), []);
  const currency = useLive(() => runtime.mirror.session(), null)?.tenant.currency ?? 'USD';

  const edit = useCallback((next: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...next }));
    setSaved(false);
  }, []);

  const save = useCallback(async () => {
    try {
      await runtime.api.request({
        method: 'PATCH',
        path: `/newsletter/issues/${issue.issue_id}`,
        body: { subject: draft.subject, preheader: draft.preheader, blocks: draft.blocks },
        schema: NewsletterIssue,
      });
      setSaved(true);
      setNotice(null);
    } catch (e) {
      setNotice(e instanceof ApiFailure ? e.message : String(e));
    }
  }, [runtime, issue.issue_id, draft]);

  const look = useCallback(async () => {
    try {
      if (!saved) await save();
      const got = await runtime.api.request({
        method: 'GET',
        path: `/newsletter/issues/${issue.issue_id}/preview`,
        schema: NewsletterPreview,
      });
      setPreview(got);
      setNotice(null);
    } catch (e) {
      setNotice(e instanceof ApiFailure ? e.message : String(e));
    }
  }, [runtime, issue.issue_id, saved, save]);

  const send = useCallback(
    async (testTo: string | null) => {
      try {
        if (!saved) await save();
        const result = await runtime.api.request({
          method: 'POST',
          path: `/newsletter/issues/${issue.issue_id}/send`,
          body: testTo === null ? {} : { test_to: testTo },
          schema: NewsletterSendResult,
        });
        setNotice(
          result.test
            ? t('newsletter.testQueued')
            : t('newsletter.queued', { count: result.queued }),
        );
        if (!result.test) onClose();
      } catch (e) {
        setNotice(e instanceof ApiFailure ? e.message : String(e));
      }
    },
    [runtime, issue.issue_id, saved, save, onClose, t],
  );

  const myEmail = useUser()?.email ?? null;

  return (
    <section className="view view--split">
      <div className="view__header">
        <button type="button" className="link-button" onClick={onClose}>
          {t('common.close')}
        </button>
        <h1>{draft.subject === '' ? t('newsletter.untitled') : draft.subject}</h1>
      </div>

      {notice === null ? null : <p className="panel panel--warn">{notice}</p>}

      <div className="pane">
        <label className="field">
          <span>{t('newsletter.subject')}</span>
          <input
            value={draft.subject}
            disabled={readOnly}
            maxLength={150}
            onChange={(e) => edit({ subject: e.target.value })}
          />
        </label>
        <label className="field">
          <span>{t('newsletter.preheader')}</span>
          <input
            value={draft.preheader}
            disabled={readOnly}
            maxLength={200}
            placeholder={t('newsletter.preheaderHint')}
            onChange={(e) => edit({ preheader: e.target.value })}
          />
        </label>

        <BlockList
          blocks={draft.blocks}
          products={products ?? []}
          currency={currency}
          locale={locale}
          readOnly={readOnly}
          onChange={(blocks) => edit({ blocks })}
        />

        {readOnly ? null : (
          <div className="editor editor--inline">
            <button type="button" className="button button--primary" onClick={() => void save()}>
              {saved ? t('newsletter.saved') : t('common.save')}
            </button>
            <button type="button" className="button" onClick={() => void look()}>
              {t('newsletter.preview')}
            </button>
            {myEmail === null ? null : (
              <button type="button" className="button" onClick={() => void send(myEmail)}>
                {t('newsletter.testMe', { email: myEmail })}
              </button>
            )}
            <button
              type="button"
              className="button button--primary"
              onClick={() => {
                if (window.confirm(t('newsletter.confirmSend'))) void send(null);
              }}
            >
              {t('newsletter.sendNow')}
            </button>
          </div>
        )}
      </div>

      {preview === null ? null : (
        <div className="pane">
          <div className="editor editor--inline">
            <button type="button" className="button" onClick={() => setWidth('desktop')}>
              {t('newsletter.desktop')}
            </button>
            <button type="button" className="button" onClick={() => setWidth('phone')}>
              {t('newsletter.phone')}
            </button>
          </div>
          {preview.blockers.map((s) => (
            <p className="error" key={s}>
              {s}
            </p>
          ))}
          {preview.warnings.map((s) => (
            <p className="muted" key={s}>
              {s}
            </p>
          ))}
          <p className="muted">{preview.from}</p>
          {/**
           * A sandboxed `srcdoc` iframe, and the sandbox is the whole reason it
           * is an iframe at all. The document is the tenant's own words — but
           * it is also, on any tenant, a document assembled out of strings
           * people typed into forms, and rendering it inside this app's DOM
           * would give it this app's origin and this app's signed-in session.
           * `sandbox` with no `allow-same-origin` and no `allow-scripts` is an
           * inert picture of the email.
           */}
          <iframe
            title={t('newsletter.preview')}
            srcDoc={preview.html}
            sandbox=""
            style={{
              width: width === 'phone' ? '390px' : '100%',
              maxWidth: '100%',
              height: '70vh',
              border: '1px solid rgba(0,0,0,0.12)',
              borderRadius: '8px',
              background: '#fff',
            }}
          />
        </div>
      )}

      {issue.status === 'sent' || issue.status === 'sending' ? (
        <div className="panel">
          <p>
            {t('newsletter.counts', {
              sent: issue.stats.sent,
              failed: issue.stats.failed + issue.stats.bounced,
              unsubscribed: issue.stats.unsubscribed,
            })}
          </p>
          <p className="muted">{t('newsletter.noTracking')}</p>
        </div>
      ) : null}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Blocks                                                              */
/* ------------------------------------------------------------------ */

const KINDS = ['heading', 'text', 'image', 'product', 'button', 'divider'] as const;

function blankBlock(kind: (typeof KINDS)[number], firstProduct: string | null): NewsletterBlock {
  switch (kind) {
    case 'heading':
      return { kind: 'heading', text: '', level: 2 };
    case 'text':
      return { kind: 'text', text: '' };
    case 'image':
      return { kind: 'image', url: '', alt: '' };
    case 'product':
      return { kind: 'product', item_id: firstProduct ?? '' };
    case 'button':
      return { kind: 'button', label: '', url: '' };
    case 'divider':
      return { kind: 'divider' };
  }
}

function BlockList({
  blocks,
  products,
  currency,
  locale,
  readOnly,
  onChange,
}: {
  blocks: NewsletterBlock[];
  products: Product[];
  currency: string;
  locale: UiLocale;
  readOnly: boolean;
  onChange: (blocks: NewsletterBlock[]) => void;
}): ReactNode {
  const t = useT();
  const sellable = useMemo(() => products.filter((p) => p.active && !p.open_price), [products]);

  const replace = (index: number, block: NewsletterBlock): void =>
    onChange(blocks.map((b, i) => (i === index ? block : b)));
  const move = (index: number, by: number): void => {
    const next = [...blocks];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    const a = next[index] as NewsletterBlock;
    next[index] = next[target] as NewsletterBlock;
    next[target] = a;
    onChange(next);
  };

  return (
    <div className="editor">
      {blocks.map((block, index) => (
        <div className="panel" key={`${block.kind}-${index}`}>
          <div className="editor editor--inline">
            <strong>{t(`newsletter.block.${block.kind}` as 'newsletter.block.text')}</strong>
            {readOnly ? null : (
              <>
                <button type="button" className="link-button" onClick={() => move(index, -1)}>
                  ↑
                </button>
                <button type="button" className="link-button" onClick={() => move(index, 1)}>
                  ↓
                </button>
                <button
                  type="button"
                  className="link-button link-button--danger"
                  onClick={() => onChange(blocks.filter((_, i) => i !== index))}
                >
                  {t('common.remove')}
                </button>
              </>
            )}
          </div>
          <BlockEditor
            block={block}
            products={sellable}
            currency={currency}
            locale={locale}
            readOnly={readOnly}
            onChange={(next) => replace(index, next)}
          />
        </div>
      ))}

      {readOnly ? null : (
        <div className="editor editor--inline">
          {KINDS.map((kind) => (
            <button
              type="button"
              className="button"
              key={kind}
              onClick={() =>
                onChange([...blocks, blankBlock(kind, sellable[0]?.product_id ?? null)])
              }
            >
              + {t(`newsletter.block.${kind}` as 'newsletter.block.text')}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BlockEditor({
  block,
  products,
  currency,
  locale,
  readOnly,
  onChange,
}: {
  block: NewsletterBlock;
  products: Product[];
  currency: string;
  locale: UiLocale;
  readOnly: boolean;
  onChange: (block: NewsletterBlock) => void;
}): ReactNode {
  const t = useT();

  if (block.kind === 'divider') return null;

  if (block.kind === 'heading') {
    return (
      <label className="field">
        <span>{t('newsletter.text')}</span>
        <input
          value={block.text}
          disabled={readOnly}
          maxLength={120}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
        />
      </label>
    );
  }

  if (block.kind === 'text') {
    return (
      <label className="field">
        <span>{t('newsletter.text')}</span>
        <textarea
          rows={5}
          value={block.text}
          disabled={readOnly}
          maxLength={4000}
          placeholder={t('newsletter.textHint')}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
        />
      </label>
    );
  }

  if (block.kind === 'image') {
    return (
      <>
        <label className="field">
          <span>{t('newsletter.imageUrl')}</span>
          <input
            value={block.url}
            disabled={readOnly}
            placeholder="https://…"
            onChange={(e) => onChange({ ...block, url: e.target.value })}
          />
        </label>
        <label className="field">
          <span>{t('newsletter.alt')}</span>
          <input
            value={block.alt}
            disabled={readOnly}
            maxLength={200}
            placeholder={t('newsletter.altHint')}
            onChange={(e) => onChange({ ...block, alt: e.target.value })}
          />
        </label>
      </>
    );
  }

  if (block.kind === 'product') {
    return (
      <>
        <label className="field">
          <span>{t('newsletter.product')}</span>
          <select
            value={block.item_id}
            disabled={readOnly}
            onChange={(e) => onChange({ ...block, item_id: e.target.value })}
          >
            <option value="">—</option>
            {products.map((p) => (
              <option value={p.product_id} key={p.product_id}>
                {`${p.name} · ${formatMoney(p.price_cents, currency, locale)}`}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('newsletter.blurb')}</span>
          <textarea
            rows={2}
            value={block.blurb ?? ''}
            disabled={readOnly}
            maxLength={400}
            onChange={(e) => onChange({ ...block, blurb: e.target.value })}
          />
        </label>
        {/* The price is not editable here, and the sentence says why. */}
        <p className="muted">{t('newsletter.priceFromCatalog')}</p>
      </>
    );
  }

  return (
    <>
      <label className="field">
        <span>{t('newsletter.label')}</span>
        <input
          value={block.label}
          disabled={readOnly}
          maxLength={60}
          onChange={(e) => onChange({ ...block, label: e.target.value })}
        />
      </label>
      <label className="field">
        <span>{t('newsletter.linkUrl')}</span>
        <input
          value={block.url}
          disabled={readOnly}
          placeholder="https://…"
          onChange={(e) => onChange({ ...block, url: e.target.value })}
        />
      </label>
    </>
  );
}

/** Exported for the message-catalogue test's benefit and for `views.test.tsx`. */
export type { Draft as NewsletterDraft };
export { EMPTY as EMPTY_NEWSLETTER_DRAFT };
export type { T as NewsletterT };
